"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date/DatePicker";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertTriangle, Edit3, Save, X } from "lucide-react";
import { useVariableContext } from "../../contexts/VariableContext";
import { useComposeClientContext } from "@/lib/contexts/compose/ComposeClientContext";
import { isAutoFillVariable } from "@/lib/utils/crm/template-variables";
import { format, parseISO, isValid } from "date-fns";
import { useComposeFormStateContext } from "@/lib/contexts/compose/ComposeFormStateContext";

type ContextVariablesListProps = Record<string, never>;

export function ContextVariablesList(_props?: ContextVariablesListProps) {
  const {
    values,
    allVariables,
    autoFilledVariables,
    editingField,
    draftValue,
    getVariableValue,
    hasValue,
    getAllVariables,
    startEditing,
    updateDraft,
    applyDraft,
    cancelEditing,
  } = useVariableContext();

  const formState = useComposeFormStateContext();
  const clientState = useComposeClientContext();

  const { watchedSubject, watchedBody } = formState;
  const { selectedClient } = clientState;

  // Use centralized variables from context, fallback to extracting from subject/body if needed
  const variablesToShow =
    allVariables.length > 0 ? allVariables : getAllVariables(watchedSubject, watchedBody);

  // Context is now the single source of truth - no external sync needed

  const handleStartEditing = (varName: string): void => {
    const currentValue = getVariableValue(varName, selectedClient);
    startEditing(varName, currentValue);
  };

  const handleApply = (varName: string): void => {
    applyDraft(varName);
  };

  const editAllVariables = () => {
    const firstUnfilled = allVariables.find((varName) => !hasValue(varName, selectedClient));
    const targetVariable = firstUnfilled || allVariables[0];
    if (targetVariable) {
      handleStartEditing(targetVariable);
    }
  };

  const filledCount = variablesToShow.filter((varName) => hasValue(varName, selectedClient)).length;
  const requiredCount = variablesToShow.length - filledCount;

  return (
    <div className="space-y-4">
      {/* Simple header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h4 className="text-sm font-semibold">Variables ({variablesToShow.length})</h4>
          <div className="flex items-center gap-2 text-xs">
            {filledCount > 0 && (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle className="h-3 w-3" />
                {filledCount} Filled
              </span>
            )}
            {requiredCount > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertTriangle className="h-3 w-3" />
                {requiredCount} Required
              </span>
            )}
          </div>
        </div>

        {variablesToShow.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={editAllVariables}
            className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
          >
            <Edit3 className="h-3 w-3 mr-1" />
            Edit
          </Button>
        )}
      </div>

      {/* Variable list - always shows all variables */}
      <div className="space-y-3">
        {variablesToShow.map((varName: string) => {
          const currentValue = getVariableValue(varName, selectedClient);
          const isEditing = editingField === varName;
          const filled = hasValue(varName, selectedClient);
          const isAutoFill = isAutoFillVariable(varName);
          const hasAutoFilledValue =
            autoFilledVariables[varName] && autoFilledVariables[varName].trim() !== "";
          const hasManualValue = values[varName] && values[varName].trim() !== "";

          return (
            <div
              key={varName}
              className={`border rounded-lg p-3 ${isAutoFill && !hasManualValue ? "bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800" : ""}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {filled ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                )}
                <code className="text-sm font-mono bg-muted px-1 rounded">{`{{${varName}}}`}</code>
                {filled && (
                  <Badge variant="outline" className="h-4 text-xs text-green-600 border-green-300 dark:border-green-800">
                    Filled
                  </Badge>
                )}
                {isAutoFill && hasAutoFilledValue && !hasManualValue && (
                  <Badge variant="secondary" className="h-4 text-xs text-blue-600 border-blue-300 dark:border-blue-800">
                    Auto-filled
                  </Badge>
                )}
                {isAutoFill && hasAutoFilledValue && hasManualValue && (
                  <Badge
                    variant="outline"
                    className="h-4 text-xs text-purple-600 border-purple-300 dark:border-purple-800"
                  >
                    Manual override
                  </Badge>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-2">
                  {(() => {
                    // Check if this is a date variable
                    const isDateVariable =
                      varName === "kickoff_date" || varName === "proposal_date";

                    // Parse date value for date picker
                    const getDateValue = (value: string | undefined): string | undefined => {
                      if (!value) return undefined;
                      // Try to parse as ISO date
                      const parsed = parseISO(value);
                      if (isValid(parsed)) {
                        return format(parsed, "yyyy-MM-dd");
                      }
                      // Try to parse common date formats
                      const date = new Date(value);
                      if (isValid(date)) {
                        return format(date, "yyyy-MM-dd");
                      }
                      return undefined;
                    };

                    const dateValue = isDateVariable
                      ? getDateValue(draftValue || currentValue)
                      : undefined;

                    if (isDateVariable) {
                      return (
                        <DatePicker
                          value={dateValue}
                          onChange={(date) => updateDraft(date)}
                          placeholder={`Select ${varName.replace("_", " ")}...`}
                          className="w-full text-sm"
                        />
                      );
                    } else if (varName.includes("body") || varName.includes("description")) {
                      return (
                        <Textarea
                          value={draftValue}
                          onChange={(e) => updateDraft(e.target.value)}
                          placeholder={`Enter value for ${varName}`}
                          className="text-sm"
                          rows={3}
                          autoFocus
                        />
                      );
                    } else {
                      return (
                        <Input
                          value={draftValue}
                          onChange={(e) => updateDraft(e.target.value)}
                          placeholder={`Enter value for ${varName}`}
                          className="text-sm"
                          autoFocus
                        />
                      );
                    }
                  })()}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApply(varName)}
                      className="h-6 px-2 text-xs"
                    >
                      <Save className="h-3 w-3 mr-1" />
                      Apply
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelEditing}
                      className="h-6 px-2 text-xs"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {currentValue ? (
                      <p className="text-sm text-foreground bg-muted/30 p-2 rounded border">
                        {(() => {
                          // Check if this is a date variable and format it
                          const isDateVariable =
                            varName === "kickoff_date" || varName === "proposal_date";
                          if (isDateVariable) {
                            const parsed = parseISO(currentValue);
                            if (isValid(parsed)) {
                              return format(parsed, "MM-dd-yyyy");
                            }
                            const date = new Date(currentValue);
                            if (isValid(date)) {
                              return format(date, "MM-dd-yyyy");
                            }
                          }
                          return currentValue;
                        })()}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No value set</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStartEditing(varName)}
                    className="ml-2 h-6 px-2 text-xs"
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {allVariables.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p>No template variables found in this email.</p>
        </div>
      )}
    </div>
  );
}
